import * as yup from 'yup';
import { DateTime } from 'luxon';
import { RegistrationMarriageFormVM } from 'mutation/view-models/registration-marriage';
import { idActRegex } from 'shell/constants/regex';
import { ActType } from 'shell/constants/act-type';
import { DocumentDigital } from 'shell/view-models/act-form';

export const validationSchema = yup
  .object()
  .shape({
    editDataControl: yup
      .object()
      .nullable()
      .shape({
        dateValidity: yup
          .date()
          .nullable()
          .required('campo obbligatorio')
          .typeError('Data non valida'),
      }),
    marriage: yup
      .object()
      .nullable()
      .shape({
        person: yup.object().nullable().shape({
          surname: yup.string().nullable(),
          name: yup.string().nullable(),
          withoutName: yup.boolean().nullable(),
          withoutSurname: yup.boolean().nullable(),
        }),
        eventDate: yup
          .date()
          .nullable()
          .typeError('Data non valida')
          .required('campo obbligatorio')
          .max(DateTime.now(), 'La data deve essere minore del giorno corrente'),
        isForeign: yup.boolean().nullable().notRequired(),
        italian: yup
          .object()
          .nullable()
          .shape({
            municipality: yup.object().nullable().shape({
              description: yup.string().nullable(),
            }),
          }),
        foreign: yup
          .object()
          .nullable()
          .shape({
            municipality: yup.object().nullable().shape({
              description: yup.string().nullable(),
            }),
          }),
        // start act
        actType: yup.string(),
        digital: yup
          .object()
          .nullable()
          .when('actType', {
            is: ActType.AttoDigitale,
            otherwise: yup.object().nullable(),
            then: yup
              .object()
              .nullable()
              .shape({
                idAnsc: yup
                  .string()
                  .nullable()
                  .matches(idActRegex, 'Formato non corretto')
                  .test((value) => {
                    if (value) {
                      const [year] = value.split('-');
                      const isValid = year ? Number.parseInt(year) >= 2022 : true;

                      return isValid
                        ? true
                        : new yup.ValidationError(
                            "L'idAnsc deve avere anno maggiore al 2022",
                            undefined,
                            'marriage.digital.idAnsc'
                          );
                    }
                    return true;
                  })
                  .required('Campo richiesto'),
                documentDate: yup.date().nullable().notRequired().typeError('Formato non valido'),
                municipality: yup.object().shape({}).nullable().required('Campo richiesto'),
              })
              .test((values) => {
                const castedValues = values as unknown as DocumentDigital;

                if (
                  castedValues?.municipality?.id &&
                  castedValues?.istatCode &&
                  castedValues?.municipality?.id === castedValues.istatCode
                ) {
                  return true;
                }
                return new yup.ValidationError([
                  new yup.ValidationError(
                    'Non coincidono i due codici ISTAT',
                    undefined,
                    'marriage.digital.municipality'
                  ),
                  new yup.ValidationError(
                    'Non coincidono i due codici ISTAT',
                    undefined,
                    'marriage.digital.istatCode'
                  ),
                ]);
              }),
          }),
        paper: yup
          .object()
          .nullable()
          .when('actType', {
            is: ActType.AttoCartaceo,
            otherwise: yup.object().nullable(),
            then: yup.object().shape({
              actDate: yup
                .date()
                .nullable()
                .max(
                  DateTime.local().plus({ days: 1 }),
                  'La data non può essere successiva alla data odierna'
                )
                .typeError('Data non valida'),
              transcribed: yup.string().nullable(),
              actMunicipality: yup.object().nullable().shape({}).required('Il campo è richiesto'),
              actYear: yup
                .number()
                .nullable()
                .required('Campo richiesto')
                .typeError('Sono ammesse solamente cifre'),
              actNumber: yup
                .number()
                .nullable()
                .required('Campo richiesto')
                .typeError('Sono ammesse solamente cifre'),
              actPart: yup
                .string()
                .nullable()
                .required('Campo richiesto')
                .test(
                  'part-ammisible-values',
                  'Gli unici valori ammessi sono 1 o 2',
                  (value) => ['1', '2'].indexOf(value || '') >= 0
                ),
              actSeries: yup.string().nullable(),
              actVolume: yup
                .string()
                .nullable()
                .when('actMunicipality.description', {
                  is: (description: string) => (description || '').toLocaleLowerCase() === 'milano',
                  then: yup
                    .string()
                    // .required('Campo obbligatorio')
                    .matches(/^R[0-9]{2}$/, {
                      message:
                        'Per il comune di Milano il volume è formato da R e due cifre(es. R00)',
                    }),
                }),
            }),
          }),
        // end act
        generality: yup.object().nullable().shape({
          gender: yup.string().nullable(),
        }),
      }),
    searchMarriage: yup.object().nullable().shape({
      surname: yup.string().nullable(),
      name: yup.string().nullable(),
      withoutName: yup.boolean().nullable(),
      withoutSurname: yup.boolean().nullable(),
      gender: yup.string().nullable(),
    }),
  })
  // controllo luogo eccezionale
  .test('marriage.exceptionalLocation', 'Controllo', (values) => {
    const castedValues = values as unknown as RegistrationMarriageFormVM;

    if (
      (castedValues.marriage?.italian?.municipality?.description ||
        castedValues.marriage?.foreign?.municipality?.description) &&
      castedValues.marriage?.exceptionalLocation
    ) {
      return new yup.ValidationError(
        'Inserire o Luogo eccezionale o Evento Italia/Estero',
        undefined,
        'marriage.exceptionalLocation'
      );
    }

    return false;
  })
  // controlli italia / estero
  .test('marriage.italian.municipality', 'Campo obbligatorio', (values) => {
    const castedValues = values as unknown as RegistrationMarriageFormVM;

    // controllo italiano
    if (
      !castedValues.marriage?.isForeign &&
      !castedValues.marriage?.italian?.municipality?.description
    ) {
      return new yup.ValidationError(
        'Campo obbligatorio',
        undefined,
        'marriage.italian.municipality'
      );
    }

    return false;
  })
  .test('marriage.foreign.municipality', 'Campo obbligatorio', (values) => {
    const castedValues = values as unknown as RegistrationMarriageFormVM;

    // controllo estero
    if (
      castedValues.marriage?.isForeign &&
      !castedValues.marriage?.foreign?.municipality?.description
    ) {
      return new yup.ValidationError(
        'Campo obbligatorio',
        undefined,
        'marriage.foreign.municipality'
      );
    }

    return false;
  })
  // controlli ATTO se presente un valore tutto l'oggetto è obbligatorio
  .test('marriage.municipality', 'Campo obbligatorio', (values) => {
    const castedValues = values as unknown as RegistrationMarriageFormVM;

    const isPresentOtherValues = Boolean(
      castedValues.marriage?.paper?.actYear ||
        castedValues.marriage?.paper?.actPart ||
        castedValues.marriage?.paper?.actNumber ||
        castedValues.marriage?.paper?.actVolume ||
        castedValues.marriage?.paper?.actSeries ||
        castedValues.marriage?.paper?.actDate
    );
    // controllo comune
    if (
      isPresentOtherValues &&
      castedValues.marriage?.actType === ActType.AttoCartaceo &&
      !castedValues.marriage?.paper?.actMunicipality?.description
    ) {
      return new yup.ValidationError(
        'Campo obbligatorio',
        undefined,
        'marriage.paper.actMunicipality'
      );
    }

    return false;
  })
  .test('marriage.year', 'Campo obbligatorio', (values) => {
    const castedValues = values as unknown as RegistrationMarriageFormVM;

    const isPresentOtherValues = Boolean(
      castedValues.marriage?.paper?.actMunicipality?.description ||
        castedValues.marriage?.paper?.actPart ||
        castedValues.marriage?.paper?.actNumber ||
        castedValues.marriage?.paper?.actVolume ||
        castedValues.marriage?.paper?.actSeries ||
        castedValues.marriage?.paper?.actDate
    );
    // controllo anno
    if (
      isPresentOtherValues &&
      castedValues.marriage?.actType === ActType.AttoCartaceo &&
      !castedValues.marriage?.paper?.actYear
    ) {
      return new yup.ValidationError('Campo obbligatorio', undefined, 'marriage.paper.actYear');
    }

    return false;
  })
  .test('marriage.portion', 'Campo obbligatorio', (values) => {
    const castedValues = values as unknown as RegistrationMarriageFormVM;

    const isPresentOtherValues = Boolean(
      castedValues.marriage?.paper?.actMunicipality?.description ||
        castedValues.marriage?.paper?.actYear ||
        castedValues.marriage?.paper?.actNumber ||
        castedValues.marriage?.paper?.actVolume ||
        castedValues.marriage?.paper?.actSeries ||
        castedValues.marriage?.paper?.actDate
    );
    // controllo parte
    if (
      isPresentOtherValues &&
      castedValues.marriage?.actType === ActType.AttoCartaceo &&
      !castedValues.marriage?.paper?.actPart
    ) {
      return new yup.ValidationError('Campo obbligatorio', undefined, 'marriage.paper.actPart');
    }

    return false;
  })
  .test('marriage.documentNumber', 'Campo obbligatorio', (values) => {
    const castedValues = values as unknown as RegistrationMarriageFormVM;

    const isPresentOtherValues = Boolean(
      castedValues.marriage?.paper?.actMunicipality?.description ||
        castedValues.marriage?.paper?.actYear ||
        castedValues.marriage?.paper?.actPart ||
        castedValues.marriage?.paper?.actVolume ||
        castedValues.marriage?.paper?.actSeries ||
        castedValues.marriage?.paper?.actDate
    );
    // controllo numero documento
    if (
      isPresentOtherValues &&
      castedValues.marriage?.actType === ActType.AttoCartaceo &&
      !castedValues.marriage?.paper?.actNumber
    ) {
      return new yup.ValidationError('Campo obbligatorio', undefined, 'marriage.paper.actNumber');
    }

    return false;
  })
  .test(
    'marriage.documentDate',
    'Data atto non può essere superiore alla data evento',
    (values) => {
      const castedValues = values as unknown as RegistrationMarriageFormVM;

      // controllo data atto
      if (
        castedValues.marriage?.eventDate &&
        castedValues.marriage?.paper?.actDate &&
        castedValues.marriage?.actType === ActType.AttoCartaceo &&
        castedValues.marriage?.paper?.actDate < castedValues.marriage?.eventDate
      ) {
        return new yup.ValidationError(
          'Data atto deve essere superiore o uguale alla data evento',
          undefined,
          'marriage.documentDate'
        );
      }

      return false;
    }
  )
  // controllo data ATTO
  // controlli coniuge
  // surname
  .test('searchMarriage.municipality', 'Campo obbligatorio', (values) => {
    const castedValues = values as unknown as RegistrationMarriageFormVM;

    // controllo searchMarriage surname
    if (
      !castedValues.marriage?.person?.surname &&
      !castedValues.marriage?.person?.withoutSurname &&
      !castedValues.searchMarriage?.surname &&
      !castedValues.searchMarriage?.withoutSurname
    ) {
      return new yup.ValidationError('Campo obbligatorio', undefined, 'searchMarriage.surname');
    }

    return false;
  })
  .test('marriage.person.surname', 'Campo obbligatorio', (values) => {
    const castedValues = values as unknown as RegistrationMarriageFormVM;

    // controllo person surname
    if (
      !castedValues.searchMarriage?.surname &&
      !castedValues.searchMarriage?.withoutSurname &&
      !castedValues.marriage?.person?.withoutSurname &&
      !castedValues.marriage?.person?.surname
    ) {
      return new yup.ValidationError('Campo obbligatorio', undefined, 'marriage.person.surname');
    }

    return false;
  })
  // name
  .test('searchMarriage.name', 'Campo obbligatorio', (values) => {
    const castedValues = values as unknown as RegistrationMarriageFormVM;

    // controllo searchMarriage name
    if (
      !castedValues.marriage?.person?.name &&
      !castedValues.marriage?.person?.withoutName &&
      !castedValues.searchMarriage?.name &&
      !castedValues.searchMarriage?.withoutName
    ) {
      return new yup.ValidationError('Campo obbligatorio', undefined, 'searchMarriage.name');
    }

    return false;
  })
  .test('marriage.person.name', 'Campo obbligatorio', (values) => {
    const castedValues = values as unknown as RegistrationMarriageFormVM;

    // controllo person name
    if (
      !castedValues.searchMarriage?.name &&
      !castedValues.searchMarriage?.withoutName &&
      !castedValues.marriage?.person?.withoutName &&
      !castedValues.marriage?.person?.name
    ) {
      return new yup.ValidationError('Campo obbligatorio', undefined, 'marriage.person.name');
    }

    return false;
  })
  // gender
  .test('searchMarriage.gender', 'Campo obbligatorio', (values) => {
    const castedValues = values as unknown as RegistrationMarriageFormVM;

    // controllo searchMarriage gender
    if (!castedValues.marriage?.generality?.gender && !castedValues.searchMarriage?.gender) {
      return new yup.ValidationError('Campo obbligatorio', undefined, 'searchMarriage.gender');
    }

    return false;
  })
  .test('marriage.generality.gender', 'Campo obbligatorio', (values) => {
    const castedValues = values as unknown as RegistrationMarriageFormVM;

    // controllo marriage-generality gender
    if (!castedValues.marriage?.generality?.gender && !castedValues.searchMarriage?.gender) {
      return new yup.ValidationError('Campo obbligatorio', undefined, 'marriage.generality.gender');
    }

    return false;
  });
